import type { RfqViewState } from "./rfq-view-types";
import { createPortal } from "react-dom";
import { invitationStatusLabels, formatInvitationDate, rfqCountLabel } from "./rfq-presentation";

type Props = Pick<RfqViewState,
  "user" |
  "rfq" |
  "invitationLoading" |
  "invitationsLoaded" |
  "invitationError" |
  "loadInvitations" |
  "invitationDrawerSupplierId" |
  "invitationConfirm" |
  "setInvitationConfirm" |
  "drawerRef" |
  "confirmationCancelRef" |
  "confirmationTriggerRef" |
  "confirmationActionRef" |
  "closeInvitationConfirmation" |
  "invitationMessage" |
  "testPortalUrl" |
  "invitationPending" |
  "testDeliveryLoading" |
  "invite" |
  "invitationCommand" |
  "loadTestPortalUrl" |
  "closeInvitationDrawer" |
  "invitationSelection" |
  "drawerInvitation"
>;
export function RfqInvitationDialog({
  user,
  rfq,
  invitationLoading,
  invitationsLoaded,
  invitationError,
  loadInvitations,
  invitationDrawerSupplierId,
  invitationConfirm,
  setInvitationConfirm,
  drawerRef,
  confirmationCancelRef,
  confirmationTriggerRef,
  confirmationActionRef,
  closeInvitationConfirmation,
  invitationMessage,
  testPortalUrl,
  invitationPending,
  testDeliveryLoading,
  invite,
  invitationCommand,
  loadTestPortalUrl,
  closeInvitationDrawer,
  invitationSelection,
  drawerInvitation,
}: Props) {
  return (
    invitationDrawerSupplierId && invitationSelection
      ? createPortal(
          <div className="rfq-invitation-drawer-layer">
            <div
              className="rfq-invitation-drawer-backdrop"
              aria-hidden="true"
              onClick={closeInvitationDrawer}
            />
            <aside
              ref={drawerRef}
              className="rfq-invitation-drawer"
              role="dialog"
              aria-modal="true"
              aria-labelledby="rfq-invitation-drawer-title"
            >
              <header>
                <div>
                  <span className="section-kicker">Tedarikçi erişimi</span>
                  <h2 id="rfq-invitation-drawer-title">
                    Tedarikçi Davetini Yönet
                  </h2>
                  <p>
                    {invitationSelection.supplier_name ??
                      invitationSelection.supplierName}
                  </p>
                </div>
                <button
                  className="icon-button"
                  aria-label="Davet yönetimini kapat"
                  onClick={closeInvitationDrawer}
                >
                  ×
                </button>
              </header>
              <div className="rfq-invitation-drawer-body">
                {invitationError ? (
                  <div className="error-state" role="alert">
                    <span>{invitationError}</span>
                    <button
                      className="secondary-button"
                      onClick={() => rfq && loadInvitations(rfq.id)}
                    >
                      Yeniden dene
                    </button>
                  </div>
                ) : null}
                {invitationMessage ? (
                  <p className="success-state" role="status">
                    {invitationMessage}
                  </p>
                ) : null}
                {invitationLoading ? (
                  <p role="status">Davet ayrıntıları yükleniyor…</p>
                ) : drawerInvitation ? (
                  <>
                    <section
                      className="rfq-invitation-overview"
                      aria-label="Davet özeti"
                    >
                      <span
                        className={`rfq-invitation-state rfq-invitation-state-${drawerInvitation.status.toLowerCase()}`}
                      >
                        {invitationStatusLabels[drawerInvitation.status] ??
                          "Bilinmiyor"}
                      </span>
                      <dl>
                        <div>
                          <dt>İletişim kişisi</dt>
                          <dd>
                            {drawerInvitation.contactName ?? "Belirtilmedi"}
                          </dd>
                        </div>
                        <div>
                          <dt>Alıcı</dt>
                          <dd>{drawerInvitation.recipient}</dd>
                        </div>
                        <div>
                          <dt>Gönderim durumu</dt>
                          <dd>
                            {invitationStatusLabels[
                              drawerInvitation.queueStatus ?? ""
                            ] ?? "Bilinmiyor"}
                          </dd>
                        </div>
                        <div>
                          <dt>Gönderim denemesi</dt>
                          <dd>{rfqCountLabel(drawerInvitation.attemptCount)}</dd>
                        </div>
                        <div>
                          <dt>Bağlantı geçerliliği</dt>
                          <dd>
                            {formatInvitationDate(drawerInvitation.expiresAt)}
                          </dd>
                        </div>
                      </dl>
                      {drawerInvitation.queueStatus === "DEAD" ? (
                        <p className="rfq-invitation-context warning">
                          Gönderim deneme sınırına ulaştı. Sorunu kontrol edip
                          daveti yeniden gönderin.
                        </p>
                      ) : drawerInvitation.status === "REVOKED" ? (
                        <p className="rfq-invitation-context">
                          Bu davet ve bağlı oturumlar iptal edildi.
                        </p>
                      ) : null}
                      {user.preview === true &&
                      drawerInvitation.status !== "REVOKED" ? (
                        <div className="rfq-test-delivery">
                          <strong>Test tedarikçi erişimi</strong>
                          <p>
                            Bu bağlantı yalnız demo ortamındaki tedarikçi
                            panelini açar; gerçek e-posta göndermez.
                          </p>
                          {testPortalUrl ? (
                            <a
                              className="secondary-button"
                              href={testPortalUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Tedarikçi panelini aç
                            </a>
                          ) : (
                            <button
                              className="secondary-button"
                              disabled={testDeliveryLoading || !!invitationPending || !invitationsLoaded}
                              onClick={() =>
                                loadTestPortalUrl(drawerInvitation.id)
                              }
                            >
                              {testDeliveryLoading
                                ? "Bağlantı hazırlanıyor…"
                                : "Test bağlantısını oluştur"}
                            </button>
                          )}
                        </div>
                      ) : null}
                    </section>
                    <section
                      className="rfq-invitation-timeline"
                      aria-label="Davet geçmişi"
                    >
                      <h3>İşlem geçmişi</h3>
                      <ol>
                        <li>
                          <span>Davet oluşturuldu</span>
                          <time>
                            {formatInvitationDate(drawerInvitation.createdAt)}
                          </time>
                        </li>
                        {drawerInvitation.attemptCount ? (
                          <li>
                            <span>Gönderim denemeleri</span>
                            <strong>{drawerInvitation.attemptCount}</strong>
                          </li>
                        ) : null}
                        {drawerInvitation.revokedAt ? (
                          <li>
                            <span>Davet iptal edildi</span>
                            <time>
                              {formatInvitationDate(
                                drawerInvitation.revokedAt,
                              )}
                            </time>
                          </li>
                        ) : null}
                      </ol>
                    </section>
                  </>
                ) : (
                  <div className="table-state">
                    <strong>Davet henüz oluşturulmadı</strong>
                    <span>
                      Aktif ve doğrulanmış teklif iletişim kişisi sunucuda
                      seçilir.
                    </span>
                  </div>
                )}
              </div>
              <footer>
                {invitationConfirm ? (
                  <div
                    className="rfq-invitation-confirm"
                    role="alertdialog"
                    aria-label={
                      invitationConfirm === "resend"
                        ? "Yeniden gönderimi onayla"
                        : "İptali onayla"
                    }
                  >
                    <p>
                      {invitationConfirm === "resend"
                        ? "Eski bağlantı ve oturumlar iptal edilerek yeni bir davet oluşturulacak."
                        : "Davet bağlantısı ve açık tedarikçi oturumları hemen iptal edilecek."}
                    </p>
                    <div>
                      <button
                        ref={confirmationCancelRef}
                        className="secondary-button"
                        onClick={closeInvitationConfirmation}
                      >
                        Vazgeç
                      </button>
                      <button
                        className={
                          invitationConfirm === "revoke"
                            ? "danger-button"
                            : "approve-button"
                        }
                        disabled={!!invitationPending || testDeliveryLoading || !invitationsLoaded}
                        onClick={() =>
                          drawerInvitation &&
                          invitationCommand(
                            drawerInvitation.id,
                            invitationConfirm,
                          )
                        }
                      >
                        {invitationPending
                          ? "İşleniyor…"
                          : invitationConfirm === "resend"
                            ? "Yeniden göndermeyi onayla"
                            : "Daveti iptal et"}
                      </button>
                    </div>
                  </div>
                ) : !drawerInvitation ? (
                  <button
                    className="approve-button"
                    disabled={!!invitationPending || testDeliveryLoading || !invitationsLoaded}
                    onClick={() => invite(invitationDrawerSupplierId)}
                  >
                    {invitationPending
                      ? "Oluşturuluyor…"
                      : user.preview === true
                        ? "Test davetini gönder"
                        : "Davet e-postasını gönder"}
                  </button>
                ) : drawerInvitation.status === "REVOKED" ? (
                  <button
                    className="approve-button"
                    disabled={!!invitationPending || testDeliveryLoading || !invitationsLoaded}
                    onClick={() => invite(invitationDrawerSupplierId)}
                  >
                    Yeni davet oluştur
                  </button>
                ) : (
                  <>
                    <button
                      className="secondary-button"
                      disabled={!!invitationPending || testDeliveryLoading || !invitationsLoaded}
                      onClick={(event) => {
                        confirmationTriggerRef.current = event.currentTarget;
                        confirmationActionRef.current = "resend";
                        setInvitationConfirm("resend");
                      }}
                    >
                      Yeniden gönder
                    </button>
                    <button
                      className="danger-button"
                      disabled={!!invitationPending || testDeliveryLoading || !invitationsLoaded}
                      onClick={(event) => {
                        confirmationTriggerRef.current = event.currentTarget;
                        confirmationActionRef.current = "revoke";
                        setInvitationConfirm("revoke");
                      }}
                    >
                      Daveti iptal et
                    </button>
                  </>
                )}
              </footer>
            </aside>
          </div>,
          document.body,
        )
      : null
  );
}
